import { useState, useMemo, useEffect, useRef } from "react";
import StaffSelect from "./wizard/parts/StaffSelect";
import { cn, isInactiveEmp, userNorm } from "../utils/norms";
import { keyify } from "../utils/workflow";
import { fetchActiveServices, fetchStaffAssignments } from "../services/data";

// BMSS level hierarchy — lower index = lower seniority.
// Used to restrict non-admins from selecting staff more senior than themselves.
// Verify/update these strings against the actual LEVELDESC values in your DB.
const LEVEL_ORDER = [
  "administrative",
  "staff a",
  "staff b",
  "senior associate",
  "senior accountant",
  "manager",
  "senior manager",
  "director",
  "principal",
  "partner",
];

function getLevelRank(emp) {
  if (!emp) return 999;
  const desc = (cn(emp, "leveldesc") || cn(emp, "levelcode") || "").toLowerCase().trim();
  const idx = LEVEL_ORDER.findIndex(l => desc.includes(l) || l.includes(desc));
  return idx === -1 ? 999 : idx; // unknown levels are not filtered out
}


const ROLES = ["CP", "CM", "SP", "SM"];
const ROLE_LABELS = {
  CP: "Client Partner",
  CM: "Client Manager",
  SP: "Service Partner",
  SM: "Service Manager",
};

/**
 * BulkElectiveModal
 *
 * Props:
 *  open            – boolean
 *  onClose         – () => void
 *  onConfirm       – (tasks: BulkTask[]) => void
 *  empByIndex      – Map<staffIndex, employeeRow>
 *  userToIndex     – Map<username, staffIndex>  (to resolve current user's emp)
 *  detail          – Array of detail rows (from fetchDetail)
 *  isAdmin         – boolean
 *  currentUser     – string  (e.g. "hstaggs")
 *
 * BulkTask shape (passed to onConfirm):
 *  { clientCode, clientName, role, service, currentStaffIdx,
 *    currentStaffName, currentUsername, contIndex, newStaffIdx }
 */
export default function BulkElectiveModal({
  open,
  onClose,
  onConfirm,
  empByIndex,
  userToIndex,
  detail,
  clientGroups,
  isAdmin,
  currentUser,
}) {
  // ── Mode toggle ──────────────────────────────────────────────────────────────
  const [mode, setMode] = useState("assign"); // "assign" | "replace"

  // ── Shared state ─────────────────────────────────────────────────────────────
  const [selectedStaffIdx, setSelectedStaffIdx] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [spServices, setSpServices] = useState(new Set());
  const [smServices, setSmServices] = useState(new Set());
  const [spExpanded, setSpExpanded] = useState(false);
  const [smExpanded, setSmExpanded] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientCodes, setSelectedClientCodes] = useState(new Set());
  const [searchMode, setSearchMode] = useState("individual"); // "individual" | "group"
  const [selectedGroupParents, setSelectedGroupParents] = useState(new Set());

  // ── Assign mode state ────────────────────────────────────────────────────────
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // ── Replace mode state ───────────────────────────────────────────────────────
  const [replaceStaffIdx, setReplaceStaffIdx] = useState(null);
  const [replaceAssignments, setReplaceAssignments] = useState([]);
  const [replaceAssignmentsLoading, setReplaceAssignmentsLoading] = useState(false);

  // ── Filters state (both modes, functional but not wired to backend) ──────────
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterWipMin, setFilterWipMin] = useState("");
  const [filterWipMax, setFilterWipMax] = useState("");

  // ── Staff picklist: admins see everyone, non-admins see same level or lower ──
  const filteredEmpByIndex = useMemo(() => {
    if (isAdmin) return empByIndex;
    const myIdx = userToIndex.get(userNorm(currentUser));
    const myEmp = myIdx != null ? empByIndex.get(myIdx) : null;
    const myRank = getLevelRank(myEmp);
    const out = new Map();
    for (const [idx, emp] of empByIndex) {
      if (getLevelRank(emp) <= myRank) out.set(idx, emp);
    }
    return out;
  }, [empByIndex, userToIndex, isAdmin, currentUser]);

  // ══════════════════════════════════════════════════════════════════════════════
  //  ASSIGN MODE — derived data
  // ══════════════════════════════════════════════════════════════════════════════

  // Service options from activeAssignments (assign mode only)
  const serviceOptions = useMemo(() => {
    const set = new Set();
    for (const r of activeAssignments) {
      const svc = (cn(r, "servindex") || "").toUpperCase();
      if (svc) set.add(svc);
    }
    return [...set].sort();
  }, [activeAssignments]);

  // Deduplicated client list from detail
  const allClients = useMemo(() => {
    const map = new Map();
    for (const r of detail) {
      const code = cn(r, "clientcode");
      if (!code) continue;
      if (!map.has(code)) map.set(code, cn(r, "clientname") || code);
    }
    return [...map.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [detail]);

  // Filtered client list (assign mode search)
  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return allClients;
    return allClients.filter(
      c =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q)
    );
  }, [allClients, clientSearch]);

  // Group mode: map each parentCode to all its member clientCodes
  const groupMembersMap = useMemo(() => {
    const m = new Map();
    for (const r of (clientGroups || [])) {
      const code = cn(r, "clientcode") || r.clientCode || r.ClientCode;
      const parent = cn(r, "parentcode") || r.parentCode || r.ParentCode;
      if (!code || !parent) continue;
      if (!m.has(parent)) m.set(parent, []);
      m.get(parent).push(code);
    }
    return m;
  }, [clientGroups]);

  // Set of all client codes that have WIP
  const allClientCodeSet = useMemo(() => new Set(allClients.map(c => c.code)), [allClients]);

  // Group parents (assign mode — groups where ≥1 member has WIP)
  const groupParents = useMemo(() => {
    const parents = [];
    for (const [parentCode, members] of groupMembersMap.entries()) {
      if (!members.some(code => allClientCodeSet.has(code))) continue;
      const name = allClients.find(c => c.code === parentCode)?.name || parentCode;
      parents.push({ code: parentCode, name, memberCount: members.length });
    }
    return parents.sort((a, b) => a.code.localeCompare(b.code));
  }, [groupMembersMap, allClients, allClientCodeSet]);

  // Group list filtered by search (assign mode)
  const filteredGroups = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return groupParents;
    return groupParents.filter(g =>
      g.code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)
    );
  }, [groupParents, clientSearch]);

  // ══════════════════════════════════════════════════════════════════════════════
  //  REPLACE MODE — derived data
  // ══════════════════════════════════════════════════════════════════════════════

  // Map of replaced staff's assignments: clientCode → { name, roles, services }
  const replaceClientMap = useMemo(() => {
    const m = new Map();
    for (const r of replaceAssignments) {
      const code = cn(r, "clientcode");
      if (!code) continue;
      if (!m.has(code)) m.set(code, { name: cn(r, "clientname") || code, roles: new Set(), services: new Set() });
      const entry = m.get(code);
      const role = cn(r, "role");
      if (role) entry.roles.add(role);
      const svc = (cn(r, "servindex") || "NA").toUpperCase();
      if (svc !== "NA") entry.services.add(svc);
    }
    return m;
  }, [replaceAssignments]);

  // Replace-mode client list (searchable)
  const replaceClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const list = [...replaceClientMap.entries()].map(([code, data]) => ({
      code,
      name: data.name,
      roles: data.roles,
    }));
    list.sort((a, b) => a.code.localeCompare(b.code));
    if (!q) return list;
    return list.filter(c => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
  }, [replaceClientMap, clientSearch]);

  // Replace-mode groups (only groups where ≥1 member has the replaced staff assigned)
  const replaceGroupParents = useMemo(() => {
    if (mode !== "replace") return [];
    const assignedCodes = new Set(replaceClientMap.keys());
    const parents = [];
    for (const [parentCode, members] of groupMembersMap.entries()) {
      const matchingMembers = members.filter(c => assignedCodes.has(c));
      if (matchingMembers.length === 0) continue;
      const name = allClients.find(c => c.code === parentCode)?.name || parentCode;
      parents.push({ code: parentCode, name, memberCount: matchingMembers.length, matchingMembers });
    }
    return parents.sort((a, b) => a.code.localeCompare(b.code));
  }, [mode, replaceClientMap, groupMembersMap, allClients]);

  // Replace-mode groups filtered by search
  const filteredReplaceGroups = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return replaceGroupParents;
    return replaceGroupParents.filter(g =>
      g.code.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)
    );
  }, [replaceGroupParents, clientSearch]);

  // Available roles in Replace mode (only roles the replaced staff holds for selected clients)
  const replaceAvailableRoles = useMemo(() => {
    if (mode !== "replace") return ROLES;
    const roles = new Set();
    const codes = selectedClientCodes.size > 0 ? selectedClientCodes : new Set(replaceClientMap.keys());
    for (const code of codes) {
      const data = replaceClientMap.get(code);
      if (data) data.roles.forEach(r => roles.add(r));
    }
    return ROLES.filter(r => roles.has(r));
  }, [mode, selectedClientCodes, replaceClientMap]);

  // Replace-mode SP service options (only services where replaced staff is SP)
  const replaceSpOptions = useMemo(() => {
    if (mode !== "replace") return [];
    const svcs = new Set();
    for (const code of selectedClientCodes) {
      for (const row of replaceAssignments) {
        if (cn(row, "clientcode") !== code) continue;
        if (cn(row, "role") !== "SP") continue;
        const svc = (cn(row, "servindex") || "NA").toUpperCase();
        if (svc !== "NA") svcs.add(svc);
      }
    }
    return [...svcs].sort();
  }, [mode, selectedClientCodes, replaceAssignments]);

  // Replace-mode SM service options (only services where replaced staff is SM)
  const replaceSmOptions = useMemo(() => {
    if (mode !== "replace") return [];
    const svcs = new Set();
    for (const code of selectedClientCodes) {
      for (const row of replaceAssignments) {
        if (cn(row, "clientcode") !== code) continue;
        if (cn(row, "role") !== "SM") continue;
        const svc = (cn(row, "servindex") || "NA").toUpperCase();
        if (svc !== "NA") svcs.add(svc);
      }
    }
    return [...svcs].sort();
  }, [mode, selectedClientCodes, replaceAssignments]);

  // ══════════════════════════════════════════════════════════════════════════════
  //  EFFECTS
  // ══════════════════════════════════════════════════════════════════════════════

  // Fetch active services for selected clients (ASSIGN MODE ONLY)
  const clientCodesKey = useMemo(
    () => [...selectedClientCodes].sort().join(","),
    [selectedClientCodes]
  );

  const activeServicesReqId = useRef(0);

  useEffect(() => {
    // Only fetch active services in assign mode
    if (mode !== "assign") return;
    if (!clientCodesKey) {
      setActiveAssignments([]);
      setAssignmentsLoading(false);
      setSpServices(new Set());
      setSmServices(new Set());
      return;
    }
    setSpServices(new Set());
    setSmServices(new Set());
    setAssignmentsLoading(true);
    const codes = clientCodesKey.split(",");
    const reqId = ++activeServicesReqId.current;
    const timer = setTimeout(async () => {
      try {
        const data = await fetchActiveServices(codes);
        if (reqId !== activeServicesReqId.current) return; // stale response, discard
        setActiveAssignments(Array.isArray(data) ? data : []);
      } catch (err) {
        if (reqId !== activeServicesReqId.current) return;
        console.error("[RPRS] fetchActiveServices failed:", err);
        setActiveAssignments([]);
      } finally {
        if (reqId === activeServicesReqId.current) setAssignmentsLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [clientCodesKey, mode]);

  // Fetch staff assignments when replaceStaffIdx changes (REPLACE MODE ONLY)
  useEffect(() => {
    if (mode !== "replace" || replaceStaffIdx == null) {
      setReplaceAssignments([]);
      setReplaceAssignmentsLoading(false);
      return;
    }
    setReplaceAssignmentsLoading(true);
    setSelectedRoles(new Set());
    setSelectedClientCodes(new Set());
    setSelectedGroupParents(new Set());
    setSpServices(new Set());
    setSmServices(new Set());

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await fetchStaffAssignments(replaceStaffIdx);
        if (!cancelled) setReplaceAssignments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[RPRS] fetchStaffAssignments failed:", err);
        if (!cancelled) setReplaceAssignments([]);
      } finally {
        if (!cancelled) setReplaceAssignmentsLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [mode, replaceStaffIdx]);

  // Auto-prune roles when they become unavailable in replace mode
  useEffect(() => {
    if (mode !== "replace") return;
    const available = new Set(replaceAvailableRoles);
    setSelectedRoles(prev => {
      const next = new Set([...prev].filter(r => available.has(r)));
      return next.size !== prev.size ? next : prev;
    });
  }, [mode, replaceAvailableRoles]);

  // Clear service selections when client set changes in replace mode
  useEffect(() => {
    if (mode !== "replace") return;
    setSpServices(new Set());
    setSmServices(new Set());
  }, [clientCodesKey, mode]);

  // ══════════════════════════════════════════════════════════════════════════════
  //  PREVIEW TASKS
  // ══════════════════════════════════════════════════════════════════════════════

  const previewTasks = useMemo(() => {
    if (selectedRoles.size === 0 || selectedClientCodes.size === 0) return [];

    if (mode === "replace") {
      // ── Replace mode ─────────────────────────────────────────────────────────
      if (!selectedStaffIdx || !replaceStaffIdx) return [];
      const replaceEmp = empByIndex.get(replaceStaffIdx);
      const tasks = [];

      for (const clientCode of selectedClientCodes) {
        for (const role of selectedRoles) {
          if (role === "CP" || role === "CM") {
            const match = replaceAssignments.find(r =>
              cn(r, "clientcode") === clientCode && cn(r, "role") === role
            );
            if (!match) continue;
            const billedSum = detail.reduce((sum, r) => {
              if (cn(r, "clientcode") !== clientCode) return sum;
              return sum + Number(cn(r, "billed") || 0);
            }, 0);
            tasks.push({
              clientCode,
              clientName: cn(match, "clientname") || clientCode,
              role,
              service: "NA",
              currentStaffIdx: replaceStaffIdx,
              currentStaffName: cn(replaceEmp || {}, "staffname") || "",
              currentUsername: userNorm(cn(replaceEmp || {}, "staffuser") || ""),
              contIndex: cn(match, "contindex"),
              newStaffIdx: selectedStaffIdx,
              billed: billedSum,
            });
          } else {
            // SP/SM
            const services = role === "SP"
              ? (spServices.size > 0 ? [...spServices] : ["NA"])
              : (smServices.size > 0 ? [...smServices] : ["NA"]);
            for (const service of services) {
              const match = replaceAssignments.find(r =>
                cn(r, "clientcode") === clientCode &&
                cn(r, "role") === role &&
                (cn(r, "servindex") || "NA").toUpperCase() === service
              );
              if (!match) continue;
              const billedSum = detail.reduce((sum, r) => {
                if (cn(r, "clientcode") !== clientCode) return sum;
                if ((cn(r, "servindex") || "").toUpperCase() !== service) return sum;
                return sum + Number(cn(r, "billed") || 0);
              }, 0);
              tasks.push({
                clientCode,
                clientName: cn(match, "clientname") || clientCode,
                role,
                service,
                currentStaffIdx: replaceStaffIdx,
                currentStaffName: cn(replaceEmp || {}, "staffname") || "",
                currentUsername: userNorm(cn(replaceEmp || {}, "staffuser") || ""),
                contIndex: cn(match, "contindex"),
                newStaffIdx: selectedStaffIdx,
                billed: billedSum,
              });
            }
          }
        }
      }
      return tasks.sort((a, b) => String(a.clientCode).localeCompare(String(b.clientCode)));
    }

    // ── Assign mode ──────────────────────────────────────────────────────────
    if (!selectedStaffIdx) return [];
    const tasks = [];

    for (const clientCode of selectedClientCodes) {
      for (const role of selectedRoles) {

        if (role === "CP" || role === "CM") {
          const idxKey = role === "CP" ? "cpindex" : "cmindex";
          let bestRow = null;
          let bestBilled = -1;
          let totalBilled = 0;
          for (const r of detail) {
            if (cn(r, "clientcode") !== clientCode) continue;
            const staffIdx = Number(cn(r, idxKey, null));
            if (!Number.isFinite(staffIdx)) continue;
            const billed = Number(cn(r, "billed") || 0);
            totalBilled += billed;
            if (billed > bestBilled) { bestBilled = billed; bestRow = { r, staffIdx }; }
          }
          if (!bestRow) continue;
          const { r, staffIdx: currentStaffIdx } = bestRow;
          const currentEmp = empByIndex.get(currentStaffIdx);
          tasks.push({
            clientCode,
            clientName: cn(r, "clientname") || clientCode,
            role,
            service: "NA",
            currentStaffIdx,
            currentStaffName: cn(currentEmp || {}, "staffname") || "",
            currentUsername: userNorm(cn(currentEmp || {}, "staffuser") || ""),
            contIndex: cn(r, "contindex"),
            newStaffIdx: selectedStaffIdx,
            billed: totalBilled,
          });

        } else {
          const services = role === "SP"
            ? (spServices.size > 0 ? [...spServices] : ["NA"])
            : (smServices.size > 0 ? [...smServices] : ["NA"]);
          const idxKey  = role === "SP" ? "spindex"        : "smindex";
          const nameKey = role === "SP" ? "servicepartner" : "servicemanager";

          for (const service of services) {
            const row = activeAssignments.find(r =>
              cn(r, "clientcode") === clientCode &&
              (cn(r, "servindex") || "").toUpperCase() === service
            );
            if (!row) continue;
            const currentStaffIdx = Number(cn(row, idxKey, null));
            if (!Number.isFinite(currentStaffIdx)) continue;
            const currentEmp = empByIndex.get(currentStaffIdx);
            const billedSum = detail.reduce((sum, r) => {
              if (cn(r, "clientcode") !== clientCode) return sum;
              if ((cn(r, "servindex") || "").toUpperCase() !== service) return sum;
              return sum + Number(cn(r, "billed") || 0);
            }, 0);
            tasks.push({
              clientCode,
              clientName: cn(row, "clientname") || clientCode,
              role,
              service,
              currentStaffIdx,
              currentStaffName: cn(row, nameKey) || cn(currentEmp || {}, "staffname") || "",
              currentUsername: userNorm(cn(currentEmp || {}, "staffuser") || ""),
              contIndex: cn(row, "contindex"),
              newStaffIdx: selectedStaffIdx,
              billed: billedSum,
            });
          }
        }
      }
    }

    return tasks.sort((a, b) => String(a.clientCode).localeCompare(String(b.clientCode)));
  }, [
    mode,
    selectedStaffIdx,
    replaceStaffIdx,
    selectedRoles,
    spServices,
    smServices,
    selectedClientCodes,
    detail,
    activeAssignments,
    replaceAssignments,
    empByIndex,
  ]);

  // ══════════════════════════════════════════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════════════════════════════════════════

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setSelectedStaffIdx(null);
    setReplaceStaffIdx(null);
    setSelectedRoles(new Set());
    setSpServices(new Set());
    setSmServices(new Set());
    setSpExpanded(false);
    setSmExpanded(false);
    setActiveAssignments([]);
    setReplaceAssignments([]);
    setReplaceAssignmentsLoading(false);
    setAssignmentsLoading(false);
    setClientSearch("");
    setSelectedClientCodes(new Set());
    setSearchMode("individual");
    setSelectedGroupParents(new Set());
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
        if (role === "SP") setSpExpanded(true);
        if (role === "SM") setSmExpanded(true);
      }
      return next;
    });
  };

  const toggleClient = (code) => {
    setSelectedClientCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const selectAllFiltered = () => {
    if (searchMode === "group") {
      const groups = mode === "replace" ? filteredReplaceGroups : filteredGroups;
      setSelectedGroupParents(prev => {
        const next = new Set(prev);
        groups.forEach(g => next.add(g.code));
        return next;
      });
      setSelectedClientCodes(prev => {
        const next = new Set(prev);
        groups.forEach(g => {
          if (mode === "replace" && g.matchingMembers) {
            g.matchingMembers.forEach(c => next.add(c));
          } else {
            (groupMembersMap.get(g.code) || [g.code]).forEach(c => next.add(c));
          }
        });
        return next;
      });
    } else {
      const clients = mode === "replace" ? replaceClients : filteredClients;
      setSelectedClientCodes(prev => {
        const next = new Set(prev);
        clients.forEach(c => next.add(c.code));
        return next;
      });
    }
  };

  const clearClients = () => {
    setSelectedClientCodes(new Set());
    setSelectedGroupParents(new Set());
  };

  const toggleGroup = (parentCode) => {
    let members;
    if (mode === "replace") {
      const group = replaceGroupParents.find(g => g.code === parentCode);
      members = group ? group.matchingMembers : [parentCode];
    } else {
      members = groupMembersMap.get(parentCode) || [parentCode];
    }
    if (selectedGroupParents.has(parentCode)) {
      setSelectedGroupParents(prev => { const n = new Set(prev); n.delete(parentCode); return n; });
      setSelectedClientCodes(prev => { const n = new Set(prev); members.forEach(c => n.delete(c)); return n; });
    } else {
      setSelectedGroupParents(prev => { const n = new Set(prev); n.add(parentCode); return n; });
      setSelectedClientCodes(prev => { const n = new Set(prev); members.forEach(c => n.add(c)); return n; });
    }
  };

  const handleSearchModeToggle = (newSearchMode) => {
    setSearchMode(newSearchMode);
    setSelectedClientCodes(new Set());
    setSelectedGroupParents(new Set());
    setClientSearch("");
  };

  const toggleService = (setter, value) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleClose = () => {
    setMode("assign");
    setSelectedStaffIdx(null);
    setReplaceStaffIdx(null);
    setSelectedRoles(new Set());
    setSpServices(new Set());
    setSmServices(new Set());
    setSpExpanded(false);
    setSmExpanded(false);
    setActiveAssignments([]);
    setAssignmentsLoading(false);
    setReplaceAssignments([]);
    setReplaceAssignmentsLoading(false);
    setClientSearch("");
    setSelectedClientCodes(new Set());
    setSearchMode("individual");
    setSelectedGroupParents(new Set());
    setFiltersOpen(false);
    setFilterIndustry("");
    setFilterWipMin("");
    setFilterWipMax("");
    onClose();
  };

  const handleConfirm = () => {
    if (!previewTasks.length) return;
    onConfirm(previewTasks);
    handleClose();
  };

  // ══════════════════════════════════════════════════════════════════════════════
  //  COMPUTED — for JSX
  // ══════════════════════════════════════════════════════════════════════════════

  const newStaffName = selectedStaffIdx
    ? cn(empByIndex.get(selectedStaffIdx) || {}, "staffname")
    : "";

  const needsSpService = selectedRoles.has("SP");
  const needsSmService = selectedRoles.has("SM");

  const isLoading = mode === "replace" ? replaceAssignmentsLoading : assignmentsLoading;

  const hasServiceValidationError =
    (needsSpService && selectedClientCodes.size > 0 && !isLoading && spServices.size === 0) ||
    (needsSmService && selectedClientCodes.size > 0 && !isLoading && smServices.size === 0);

  const sameStaffError = mode === "replace"
    && replaceStaffIdx != null
    && selectedStaffIdx != null
    && replaceStaffIdx === selectedStaffIdx;

  // Roles to render as checkboxes
  const visibleRoles = mode === "replace" ? replaceAvailableRoles : ROLES;

  if (!open) return null;

  // ══════════════════════════════════════════════════════════════════════════════
  //  SHARED RENDER HELPERS
  // ══════════════════════════════════════════════════════════════════════════════

  /** Render a single SP or SM service selector section */
  const renderServiceSelector = (role, expanded, setExpanded, selectedSvcs, setSvcs) => {
    const label = role === "SP" ? "Service Partner" : "Service Manager";
    const opts = mode === "replace"
      ? (role === "SP" ? replaceSpOptions : replaceSmOptions)
      : serviceOptions;
    return (
      <div className="be-service-row">
        <div
          className="be-service-row-header"
          style={{ cursor: "pointer" }}
          onClick={() => setExpanded(v => !v)}
        >
          <span className="be-service-label">
            <span style={{ fontSize: "10px", marginRight: "4px" }}>{expanded ? "\u25BC" : "\u25B6"}</span>
            {label} — service lines:
            {selectedSvcs.size > 0
              ? <span className="be-svc-count">{selectedSvcs.size} selected</span>
              : selectedClientCodes.size === 0
                ? <span style={{ color: "#64748b", fontWeight: 600, fontSize: "11px", marginLeft: "6px" }}>select clients first</span>
                : <span style={{ color: "#b91c1c", fontWeight: 700, fontSize: "11px", marginLeft: "6px" }}>{"\u26A0"} required</span>
            }
          </span>
          <button
            type="button"
            className="be-svc-select-all"
            onClick={e => { e.stopPropagation(); setSvcs(new Set(opts)); setExpanded(true); }}
            disabled={opts.length === 0}
          >
            Select all
          </button>
        </div>
        {expanded && (
          <>
            {selectedClientCodes.size === 0 ? (
              <span style={{ fontSize: "11.5px", color: "#94a3b8", fontStyle: "italic", display: "block", marginTop: "4px" }}>
                Select client(s) to see available active service lines
              </span>
            ) : isLoading ? (
              <span style={{ fontSize: "11.5px", color: "#64748b", display: "block", marginTop: "4px" }}>
                Loading service lines…
              </span>
            ) : opts.length === 0 ? (
              <span style={{ fontSize: "11.5px", color: "#94a3b8", fontStyle: "italic", display: "block", marginTop: "4px" }}>
                No active service lines found for selected clients
              </span>
            ) : (
              <div className="be-service-chips">
                {opts.map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`be-service-chip${selectedSvcs.has(s) ? " be-service-chip--on" : ""}`}
                    onClick={() => toggleService(setSvcs, s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {!isLoading && selectedClientCodes.size > 0 && opts.length > 0 && selectedSvcs.size === 0 && (
              <span className="be-service-error">
                Select at least one service line to continue
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  /** Render the client/group selection section (shared layout, different data per mode) */
  const renderClientSection = (stepNum) => {
    const clientList = mode === "replace" ? replaceClients : filteredClients;
    const groupList = mode === "replace" ? filteredReplaceGroups : filteredGroups;
    const emptyGroupMsg = (clientGroups?.length) ? "No groups found" : "No group data loaded";
    const noAssignments = mode === "replace" && !replaceAssignmentsLoading && replaceStaffIdx != null && replaceAssignments.length === 0;

    return (
      <div className="be-section be-section--clients">
        <div className="be-section-label">
          <span className="be-step-num">{stepNum}</span>
          Select client(s)
          {searchMode === "group" && selectedGroupParents.size > 0 ? (
            <span className="be-client-badge">
              {selectedGroupParents.size} group{selectedGroupParents.size !== 1 ? "s" : ""} ({selectedClientCodes.size} clients)
            </span>
          ) : selectedClientCodes.size > 0 ? (
            <span className="be-client-badge">{selectedClientCodes.size} selected</span>
          ) : null}
          <div className="be-mode-toggle">
            <button
              type="button"
              className={`be-mode-btn${searchMode === "individual" ? " be-mode-btn--active" : ""}`}
              onClick={() => handleSearchModeToggle("individual")}
            >
              Individual(s)
            </button>
            <button
              type="button"
              className={`be-mode-btn${searchMode === "group" ? " be-mode-btn--active" : ""}`}
              onClick={() => handleSearchModeToggle("group")}
            >
              Relationship Group(s)
            </button>
          </div>
        </div>

        {mode === "replace" && replaceAssignmentsLoading && (
          <span className="be-loading-hint">Loading assignments…</span>
        )}
        {noAssignments && (
          <div className="be-warn">This staff member has no active client assignments.</div>
        )}

        {!(noAssignments || (mode === "replace" && replaceAssignmentsLoading && replaceAssignments.length === 0)) && (
          <>
            <div className="be-client-toolbar">
              <input
                className="be-client-search"
                type="text"
                placeholder={searchMode === "group" ? "Search by group code or name…" : "Search by name or client code…"}
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
              />
              <button type="button" className="be-toolbar-btn" onClick={selectAllFiltered}
                title={searchMode === "group" ? "Select all groups matching the current search" : "Select all clients matching the current search"}
              >
                Select all
              </button>
              <button type="button" className="be-toolbar-btn be-toolbar-btn--muted" onClick={clearClients} title="Clear all selections">
                Clear
              </button>
            </div>
            <div className="be-client-list">
              {searchMode === "group" ? (
                groupList.length === 0 ? (
                  <div className="be-empty">{emptyGroupMsg}</div>
                ) : (
                  groupList.map(g => (
                    <label
                      key={g.code}
                      className={`be-client-item${selectedGroupParents.has(g.code) ? " be-client-item--selected" : ""}`}
                    >
                      <input type="checkbox" checked={selectedGroupParents.has(g.code)} onChange={() => toggleGroup(g.code)} />
                      <span className="be-client-code">{g.code}</span>
                      <span className="be-client-name">{g.name}</span>
                      <span className="be-group-member-count">{g.memberCount} client{g.memberCount !== 1 ? "s" : ""}</span>
                    </label>
                  ))
                )
              ) : (
                clientList.length === 0 ? (
                  <div className="be-empty">{mode === "replace" ? "No assigned clients match your search" : "No clients found"}</div>
                ) : (
                  clientList.map(c => (
                    <label
                      key={c.code}
                      className={`be-client-item${selectedClientCodes.has(c.code) ? " be-client-item--selected" : ""}`}
                    >
                      <input type="checkbox" checked={selectedClientCodes.has(c.code)} onChange={() => toggleClient(c.code)} />
                      <span className="be-client-code">{c.code}</span>
                      <span className="be-client-name">{c.name}</span>
                      {mode === "replace" && c.roles && (
                        <span className="be-client-roles-hint">
                          {[...c.roles].sort().join(", ")}
                        </span>
                      )}
                    </label>
                  ))
                )
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  /** Render role selection section */
  const renderRolesSection = (stepNum) => (
    <div className="be-section">
      <div className="be-section-label">
        <span className="be-step-num">{stepNum}</span>
        Select role(s) to reassign
      </div>
      {visibleRoles.length === 0 ? (
        <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>
          {mode === "replace" && selectedClientCodes.size === 0
            ? "Select clients first to see available roles"
            : "No roles available for the selected combination"}
        </span>
      ) : (
        <div className="be-role-checkboxes">
          {visibleRoles.map(role => (
            <label key={role} className="be-role-check">
              <input
                type="checkbox"
                checked={selectedRoles.has(role)}
                onChange={() => toggleRole(role)}
              />
              <span className={`be-role-pill be-role-${role.toLowerCase()}`}>
                {ROLE_LABELS[role]}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Service selectors — shown when SP or SM is checked */}
      {(needsSpService || needsSmService) && (
        <div className="be-service-selectors">
          {needsSpService && renderServiceSelector("SP", spExpanded, setSpExpanded, spServices, setSpServices)}
          {needsSmService && renderServiceSelector("SM", smExpanded, setSmExpanded, smServices, setSmServices)}
        </div>
      )}
    </div>
  );

  /** Render the optional filters accordion */
  const renderFilters = () => (
    <div className="be-section be-section--filters">
      <button
        type="button"
        className="be-filters-toggle"
        onClick={() => setFiltersOpen(v => !v)}
      >
        <span style={{ fontSize: "10px", marginRight: "4px" }}>{filtersOpen ? "\u25BC" : "\u25B6"}</span>
        Filters
        <span className="be-filters-badge">Coming soon</span>
      </button>
      {filtersOpen && (
        <div className="be-filters-body">
          <div className="be-filter-row">
            <label className="be-filter-label">Industry</label>
            <select className="be-filter-input" value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}>
              <option value="">All Industries</option>
            </select>
          </div>
          <div className="be-filter-row">
            <label className="be-filter-label">Min WIP</label>
            <input type="number" className="be-filter-input" value={filterWipMin} onChange={e => setFilterWipMin(e.target.value)} placeholder="0" />
          </div>
          <div className="be-filter-row">
            <label className="be-filter-label">Max WIP</label>
            <input type="number" className="be-filter-input" value={filterWipMax} onChange={e => setFilterWipMax(e.target.value)} placeholder="No limit" />
          </div>
          <p className="be-filters-note">Filters will be applied in a future update.</p>
        </div>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  //  JSX
  // ══════════════════════════════════════════════════════════════════════════════

  return (
    <div
      className="be-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="be-modal" role="dialog" aria-modal="true" aria-label="Bulk Reassignment">

        {/* Header */}
        <div className="be-modal-header">
          <h2 className="be-modal-title">Bulk Reassignment</h2>
          <div className="be-mode-toggle be-mode-toggle--header">
            <button
              type="button"
              className={`be-mode-btn${mode === "assign" ? " be-mode-btn--active" : ""}`}
              onClick={() => handleModeChange("assign")}
            >
              Assign
            </button>
            <button
              type="button"
              className={`be-mode-btn${mode === "replace" ? " be-mode-btn--active" : ""}`}
              onClick={() => handleModeChange("replace")}
            >
              Replace
            </button>
          </div>
          <button className="be-modal-close" onClick={handleClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="be-modal-body">

          {mode === "replace" ? (
            <>
              {/* ── REPLACE MODE ───────────────────────────────────────── */}

              {/* Step 1: Staff to replace + New staff */}
              <div className="be-section">
                <div className="be-section-label">
                  <span className="be-step-num">1</span>
                  Select staff to replace and their replacement
                </div>
                <div className="be-staff-select-pair">
                  <div className="be-staff-select-col">
                    <div className="be-staff-select-sublabel">Staff to replace</div>
                    {/* Terminated staff are shown here on purpose — offboarding is the
                        most common reason to bulk-replace. The "new staff" side below
                        stays active-only. */}
                    <StaffSelect
                      value={replaceStaffIdx}
                      onChange={setReplaceStaffIdx}
                      empByIndex={filteredEmpByIndex}
                      includeInactive
                      idPrefix="replace-staff"
                    />
                  </div>
                  <div className="be-staff-select-arrow">{"\u2192"}</div>
                  <div className="be-staff-select-col">
                    <div className="be-staff-select-sublabel">New staff</div>
                    <StaffSelect
                      value={selectedStaffIdx}
                      onChange={setSelectedStaffIdx}
                      empByIndex={filteredEmpByIndex}
                      idPrefix="new-staff"
                    />
                  </div>
                </div>
                {sameStaffError && (
                  <div className="be-warn" style={{ marginTop: 8 }}>
                    The replacement staff is the same as the staff being replaced.
                  </div>
                )}
              </div>

              {/* Step 2: Clients (filtered to replaced staff's assignments) */}
              {replaceStaffIdx != null && renderClientSection(2)}

              {/* Step 3: Roles (auto-filtered) + Services */}
              {replaceStaffIdx != null && selectedClientCodes.size > 0 && renderRolesSection(3)}
            </>
          ) : (
            <>
              {/* ── ASSIGN MODE ────────────────────────────────────────── */}

              {/* Step 1: New staff */}
              <div className="be-section">
                <div className="be-section-label">
                  <span className="be-step-num">1</span>
                  Select the new staff to assign
                </div>
                <div className="be-staff-select-wrap">
                  <StaffSelect
                    value={selectedStaffIdx}
                    onChange={setSelectedStaffIdx}
                    empByIndex={filteredEmpByIndex}
                    idPrefix="assign-staff"
                  />
                </div>
              </div>

              {/* Step 2: Roles + Services */}
              {renderRolesSection(2)}

              {/* Step 3: Client selection */}
              {renderClientSection(3)}
            </>
          )}

          {/* Filters accordion (both modes) */}
          {renderFilters()}

          {/* Preview */}
          {previewTasks.length > 0 && (
            <div className="be-section be-section--preview">
              <div className="be-section-label">
                <span className="be-step-num">{mode === "replace" ? 4 : 4}</span>
                Preview — {previewTasks.length} task{previewTasks.length !== 1 ? "s" : ""} will be created
              </div>
              <div className="be-preview-scroll">
                <table className="be-preview-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Client</th>
                      <th>Role</th>
                      <th>Current Staff</th>
                      <th>New Staff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewTasks.map((t, i) => (
                      <tr key={i}>
                        <td className="be-mono">{t.clientCode}</td>
                        <td>{t.clientName}</td>
                        <td>
                          <span className={`be-role-pill be-role-${t.role.toLowerCase()}`}>
                            {ROLE_LABELS[t.role]}
                            {(t.role === "SP" || t.role === "SM") && t.service !== "NA"
                              ? `: ${t.service}`
                              : ""}
                          </span>
                        </td>
                        <td className="be-current-staff">{t.currentStaffName || "\u2014"}</td>
                        <td className="be-new-staff">{newStaffName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No matches warning */}
          {selectedStaffIdx &&
            selectedRoles.size > 0 &&
            selectedClientCodes.size > 0 &&
            previewTasks.length === 0 && (
              <div className="be-warn">
                No current role holders found for the selected combination. Verify the
                selected clients have the chosen role(s) and service line(s) assigned.
              </div>
            )}
        </div>

        {/* Footer */}
        <div className="be-modal-footer">
          <button type="button" className="ab-btn be-btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ab-btn be-btn-confirm"
            onClick={handleConfirm}
            disabled={previewTasks.length === 0 || hasServiceValidationError || sameStaffError}
          >
            Confirm &amp; Open Wizard
            {previewTasks.length > 0 && ` (${previewTasks.length} task${previewTasks.length !== 1 ? "s" : ""})`}
          </button>
        </div>
      </div>
    </div>
  );
}
